import { formatNumber } from '@/utils/currency';

interface Charge {
  label:   string;
  amount:  number; // en centimes
  /** 'f1' (primary), 'f2' (dark), 'f3' (accent), 'f4' (gray) */
  fill?:   'f1' | 'f2' | 'f3' | 'f4';
}

interface ChargesBreakdownProps {
  charges:       Charge[];
  /** KPIs ratios (taux marge brute, taux EBITDA, taux charge global) */
  ratios?: Array<{
    label: string;
    pct:   number;
    color?: 'primary' | 'success' | 'warning';
  }>;
}

const RATIO_GRAD: Record<NonNullable<NonNullable<ChargesBreakdownProps['ratios']>[number]['color']>, string> = {
  primary: 'linear-gradient(90deg, var(--p), var(--p-light))',
  success: 'linear-gradient(90deg, var(--success), #6ee7b7)',
  warning: 'linear-gradient(90deg, var(--warning), #fcd34d)',
};

/**
 * Charges breakdown pixel-perfect CTO-FRONT-002.
 * Référence : design-dashboard-finance-v1.html lignes 726-786.
 */
export const ChargesBreakdown = ({ charges, ratios }: ChargesBreakdownProps) => {
  const total = charges.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="ds-panel-body">
      {charges.map((c, i) => {
        const pct = total > 0 ? Math.round((c.amount / total) * 100) : 0;
        const fillClass = c.fill && c.fill !== 'f1' ? ` ${c.fill}` : '';
        return (
          <div key={i} className="ds-charge-item">
            <div className="ds-charge-head">
              <span className="ds-charge-name">{c.label}</span>
              <span className="ds-charge-amt">{formatNumber(c.amount / 100)}</span>
            </div>
            <div className="ds-charge-bar">
              <div className={`ds-charge-fill${fillClass}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}

      {ratios && ratios.length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--bdr-2)' }}>
          <div className="ds-panel-title" style={{ fontSize: 13, marginBottom: 12 }}>
            Indicateurs clés
          </div>
          {ratios.map((r, i) => (
            <div key={i} className="ds-kpi-progress">
              <div className="ds-kpi-pr-head">
                <span className="ds-kpi-pr-label">{r.label}</span>
                <span className="ds-kpi-pr-val">{r.pct}%</span>
              </div>
              <div className="ds-kpi-pr-bar">
                <div
                  className="ds-kpi-pr-fill"
                  style={{ width: `${Math.min(100, Math.max(0, r.pct))}%`, background: RATIO_GRAD[r.color ?? 'primary'] }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
